/**
 * Formats file size in bytes to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1.5 MB", "256 KB", "2.1 GB")
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  
  // Format to 1 decimal place, but remove .0 for whole numbers
  const formatted = size % 1 === 0 ? size.toString() : size.toFixed(1);
  
  return `${formatted} ${sizes[i]}`;
};

/**
 * Gets human-readable file type from mimetype or filename
 * @param {string} mimeType - MIME type of the file
 * @param {string} filename - Name of the file
 * @returns {string} Human-readable file type
 */
export const getFileType = (mimeType, filename) => {
  // Check MIME type first
  if (mimeType) {
    if (mimeType.startsWith('image/')) return 'Image';
    if (mimeType.startsWith('video/')) return 'Video';
    if (mimeType.startsWith('audio/')) return 'Audio File';
    if (mimeType.includes('pdf')) return 'PDF Document';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'Word Document';
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'Excel Spreadsheet';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'PowerPoint Presentation';
    if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'Archive';
    if (mimeType.includes('text/')) return 'Text File';
    if (mimeType.includes('html')) return 'HTML Document';
  }
  
  // Fallback to file extension
  if (filename) {
    const extension = filename.toLowerCase().split('.').pop();
    
    switch (extension) {
      case 'pdf': return 'PDF Document';
      case 'doc':
      case 'docx': return 'Word Document';
      case 'xls':
      case 'xlsx': return 'Excel Spreadsheet';
      case 'ppt':
      case 'pptx': return 'PowerPoint Presentation';
      case 'txt':
      case 'md':
      case 'log': return 'Text File';
      case 'html':
      case 'htm': return 'HTML Document';
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz': return 'Archive';
      case 'mp3':
      case 'wav':
      case 'flac':
      case 'ogg':
      case 'aac': return 'Audio';
      case 'mp4':
      case 'avi':
      case 'mkv':
      case 'mov':
      case 'wmv':
      case 'webm': return 'Video';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'svg':
      case 'webp': return 'Image';
      default: return 'File';
    }
  }
  
  return 'File';
};

/**
 * Gets Matrix message type from MIME type
 * @param {string} mimeType - MIME type of the file
 * @returns {string} Matrix message type (m.image, m.video, m.audio, m.file)
 */
export const getMatrixMessageType = (mimeType) => {
  if (!mimeType) return 'm.file';
  
  if (mimeType.startsWith('image/')) return 'm.image';
  if (mimeType.startsWith('video/')) return 'm.video';
  if (mimeType.startsWith('audio/')) return 'm.audio';
  
  return 'm.file';
};

/**
 * Gets the appropriate SVG icon path for attachment types
 * @param {string} msgtype - Matrix message type (m.image, m.video, m.audio, m.file)
 * @param {string} filename - Name of the file
 * @param {string} mimeType - MIME type of the file
 * @returns {string} Path to the appropriate SVG icon
 */
export const getAttachmentIcon = (msgtype, filename, mimeType) => {
  // For images and videos, use specific icons
  if (msgtype === 'm.image') {
    return '/images/svg/mediaTypeIcons/image.svg';
  }
  if (msgtype === 'm.video') {
    return '/images/svg/mediaTypeIcons/video.svg';
  }
  if (msgtype === 'm.audio') {
    return '/images/svg/mediaTypeIcons/music.svg';
  }
  
  // For files, determine by extension or mime type
  if (filename) {
    const extension = filename.toLowerCase().split('.').pop();
    
    switch (extension) {
      case 'pdf':
        return '/images/svg/mediaTypeIcons/pdf.svg';
      case 'doc':
      case 'docx':
        return '/images/svg/mediaTypeIcons/word.svg';
      case 'xls':
      case 'xlsx':
        return '/images/svg/mediaTypeIcons/excel.svg';
      case 'ppt':
      case 'pptx':
        return '/images/svg/mediaTypeIcons/powerpoint.svg';
      case 'txt':
      case 'md':
      case 'log':
        return '/images/svg/mediaTypeIcons/txt.svg';
      case 'html':
      case 'htm':
        return '/images/svg/mediaTypeIcons/html.svg';
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return '/images/svg/mediaTypeIcons/zip.svg';
      case 'mp3':
      case 'wav':
      case 'flac':
      case 'ogg':
      case 'aac':
        return '/images/svg/mediaTypeIcons/music.svg';
      case 'mp4':
      case 'avi':
      case 'mkv':
      case 'mov':
      case 'wmv':
      case 'webm':
        return '/images/svg/mediaTypeIcons/video.svg';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'svg':
      case 'webp':
        return '/images/svg/mediaTypeIcons/image.svg';
    }
  }
  
  // Check mime type as fallback
  if (mimeType) {
    if (mimeType.startsWith('image/')) {
      return '/images/svg/mediaTypeIcons/image.svg';
    }
    if (mimeType.startsWith('video/')) {
      return '/images/svg/mediaTypeIcons/video.svg';
    }
    if (mimeType.startsWith('audio/')) {
      return '/images/svg/mediaTypeIcons/music.svg';
    }
    if (mimeType.includes('pdf')) {
      return '/images/svg/mediaTypeIcons/pdf.svg';
    }
    if (mimeType.includes('word') || mimeType.includes('document')) {
      return '/images/svg/mediaTypeIcons/word.svg';
    }
    if (mimeType.includes('sheet') || mimeType.includes('excel')) {
      return '/images/svg/mediaTypeIcons/excel.svg';
    }
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) {
      return '/images/svg/mediaTypeIcons/powerpoint.svg';
    }
    if (mimeType.includes('zip') || mimeType.includes('compressed')) {
      return '/images/svg/mediaTypeIcons/zip.svg';
    }
    if (mimeType.includes('text/')) {
      return '/images/svg/mediaTypeIcons/txt.svg';
    }
    if (mimeType.includes('html')) {
      return '/images/svg/mediaTypeIcons/html.svg';
    }
  }
  
  // Default to generic file icon
  return '/images/svg/mediaTypeIcons/generic.svg';
};
